/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { memo, useCallback, useMemo, useState } from 'react';

import CommandIntelligenceStepIcon from './CommandIntelligenceStepIcon.jsx';
import { STEP_STATE_ACTIVE, STEP_STATE_DONE, STEP_STATE_PENDING } from './chat/generationStepStates';
import { hasGenerationSnapshot } from './chat/lrrProgress';
import { getCommandIntelligenceGenerationProgressStyles } from './CommandIntelligenceGenerationProgress.styles';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, Chip, Collapse, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

const resolveStepLabel = (step, intl) => {
  if (step.labelKey) {
    return intl.formatMessage({ id: step.labelKey }, step.labelValues ?? {});
  }

  if (typeof step.label === 'string' && step.label.trim() !== '') {
    return step.label;
  }

  return intl.formatMessage({ id: 'command_intelligence_reasoning_thinking' });
};

const resolveStepDetail = (step, intl) => {
  if (step.detailKey) {
    return intl.formatMessage({ id: step.detailKey }, step.detailValues ?? {});
  }

  return step.detail ?? '';
};

const resolveProgressMessage = (snapshot, intl) => {
  if (!snapshot) {
    return '';
  }

  if (snapshot.progressMessageKey) {
    return intl.formatMessage({ id: snapshot.progressMessageKey }, snapshot.progressMessageValues ?? {});
  }

  return snapshot.progressMessage ?? '';
};

const CommandIntelligenceGenerationProgress = ({ snapshot = null, isActive = false, defaultExpanded }) => {
  const intl = useIntl();
  const theme = useTheme();
  const styles = useMemo(() => getCommandIntelligenceGenerationProgressStyles(theme), [theme]);
  const [userToggled, setUserToggled] = useState(false);
  const [open, setOpen] = useState(defaultExpanded ?? isActive);

  const resolvedOpen = userToggled ? open : isActive ? true : open;

  const handleToggle = useCallback(() => {
    setUserToggled(true);
    setOpen(previous => !previous);
  }, []);

  const steps = snapshot?.steps ?? [];
  const toolCount = snapshot?.toolCount ?? 0;
  const activeStep = steps.find(step => step.state === STEP_STATE_ACTIVE);
  const doneCount = steps.filter(step => step.state === STEP_STATE_DONE).length;
  const titleId = isActive
    ? 'command_intelligence_generation_in_progress'
    : 'command_intelligence_generation_trace_title';

  const localizedProgressMessage = resolveProgressMessage(snapshot, intl);

  const collapsedSummary = localizedProgressMessage
    ? localizedProgressMessage
    : doneCount > 0 && steps.length > 0
    ? intl.formatMessage(
        { id: 'command_intelligence_generation_trace_summary' },
        { completed: doneCount, total: steps.length }
      )
    : intl.formatMessage({ id: 'command_intelligence_generation_trace_summary_generic' });

  if (!isActive && !hasGenerationSnapshot(snapshot) && !localizedProgressMessage) {
    return null;
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        <Box sx={styles.headerLeft}>
          <Box sx={styles.title}>{intl.formatMessage({ id: titleId })}</Box>
          {toolCount > 0 ? (
            <Chip
              size="small"
              label={intl.formatMessage({ id: 'command_intelligence_generation_tool_count' }, { count: toolCount })}
              sx={styles.toolChip}
            />
          ) : null}
        </Box>

        {!isActive ? (
          <Box component="button" type="button" onClick={handleToggle} sx={styles.toggleButton}>
            {intl.formatMessage({ id: 'command_intelligence_generation_trace_toggle' })}
            <ExpandMore
              sx={{
                fontSize: 18,
                transform: resolvedOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 160ms ease',
              }}
            />
          </Box>
        ) : null}
      </Box>

      {!isActive && !resolvedOpen ? <Box sx={styles.collapsedSummary}>{collapsedSummary}</Box> : null}

      <Collapse in={isActive || resolvedOpen}>
        <Box sx={styles.steps}>
          {steps.map(step => {
            const isStepActive = step.state === STEP_STATE_ACTIVE;
            const stepDetail = resolveStepDetail(step, intl);

            return (
              <Box key={step.id} sx={styles.stepRow(isStepActive)}>
                <Box sx={styles.stepIconBox}>
                  <CommandIntelligenceStepIcon state={step.state} pendingDotSx={styles.stepPendingDot} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={styles.stepLabel(isStepActive)}>{resolveStepLabel(step, intl)}</Box>
                  {stepDetail ? <Box sx={styles.stepDetail}>{stepDetail}</Box> : null}
                </Box>
              </Box>
            );
          })}
          {isActive && !activeStep && localizedProgressMessage ? (
            <Box sx={styles.subtitle}>{localizedProgressMessage}</Box>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
};

CommandIntelligenceGenerationProgress.propTypes = {
  snapshot: PropTypes.shape({
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string,
        labelKey: PropTypes.string,
        labelValues: PropTypes.object,
        state: PropTypes.oneOf([STEP_STATE_PENDING, STEP_STATE_ACTIVE, STEP_STATE_DONE]).isRequired,
        detail: PropTypes.string,
        detailKey: PropTypes.string,
        detailValues: PropTypes.object,
      })
    ),
    toolCount: PropTypes.number,
    progressMessage: PropTypes.string,
    progressMessageKey: PropTypes.string,
    progressMessageValues: PropTypes.object,
  }),
  isActive: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
};

export default memo(CommandIntelligenceGenerationProgress);
